/**
 * Scans markdown content for step-like headings (e.g. "Step 1: …", "1. Heading", "Phase 2 — …")
 * and extracts them as HowTo steps for JSON-LD structured data.
 *
 * Returns null if fewer than 3 steps are detected (not procedural enough).
 */

export interface HowToStep {
  name: string;
  text: string;
}

const STEP_HEADING_RE =
  /^#{2,3}\s+(?:step\s+\d+[.:)\-—]\s*|phase\s+\d+[.:)\-—]\s*|\d+[.)]\s+)(.+)/i;

const NUMBERED_HEADING_RE = /^#{2,3}\s+(\d+)[.:)]\s+(.+)/;

export function extractHowToSteps(markdown: string): HowToStep[] | null {
  const lines = markdown.split("\n");
  const steps: HowToStep[] = [];
  let currentStep: HowToStep | null = null;
  const bodyLines: string[] = [];

  const flushStep = () => {
    if (currentStep) {
      currentStep.text = bodyLines
        .join(" ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 300); // keep concise for schema
      if (currentStep.text) steps.push(currentStep);
    }
    bodyLines.length = 0;
  };

  for (const line of lines) {
    const stepMatch = line.match(STEP_HEADING_RE) || line.match(NUMBERED_HEADING_RE);
    if (stepMatch) {
      flushStep();
      // stepMatch may have 1 or 2 capture groups depending on regex
      const name = (stepMatch[2] || stepMatch[1]).trim();
      currentStep = { name, text: "" };
    } else if (currentStep) {
      // Collect body text (skip images, blank lines, sub-headings)
      const trimmed = line.trim();
      if (trimmed && !trimmed.startsWith("#") && !trimmed.startsWith("![")) {
        bodyLines.push(trimmed.replace(/^\s*[-*]\s+/, ""));
      }
    }
  }
  flushStep();

  return steps.length >= 3 ? steps : null;
}
